import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import withLifecycle from '@hocs/with-lifecycle';

import { components as clientComponents } from 'mage-client';
import { components as coreComponents, selectors as coreSelectors } from 'mage-core';

import { APP_PATHS, CAPS } from '@/constants';
import { getSelectableMids } from './selectors';
import { getSelectedMid, hasSelectedMid } from '@/selectors/midSelector';
import { IS_BPC_APP } from '@/selectors/widget';

export const withGlobalMids = OriginalReactComponent => {
  const WithGlobalMids = ({
    getMerchantRegion,
    getSelectableMerchants,
    merchantsWithRegion,
    mids,
    selectableMids,
    ...otherProps
  }) => {
    // This state keeps track of all regions that have already been requested merchant data from.
    // E.g. the value ['eu', 'us'] would mean that we have already called onboarding-be on those
    // regions to get the data of mids that have been onboarded there.
    const [requestedRegions, setRequestedRegions] = useState([]);

    // This effect requests the data for all the user's mids from the different regional BEs on load
    useEffect(() => {
      // When there is no token, this will be false
      if (mids?.length && merchantsWithRegion?.length) {
        getMerchants(
          // We filter out mids that we already have data for
          mids.filter(mid => !selectableMids.includes(mid)),
        );
      }
    }, [merchantsWithRegion]);

    const getMerchants = (mids = []) => {
      // Group all mids by their onboarding region
      const midsByRegion = mids.reduce((acc, mid) => {
        const region = (getMerchantRegion(mid) || 'eu').toLowerCase();
        return { ...acc, [region]: [...(acc[region] || []), mid] };
      }, {});

      const newRequestedRegions = requestedRegions;

      // For each region that we have mids from, call the corresponding BE to get merchant data
      for (const region in midsByRegion) {
        if (requestedRegions.includes(region)) {
          continue;
        }

        const regionMids = midsByRegion[region];
        if (regionMids.length) {
          getSelectableMerchants({ params: { mids: regionMids.join(',') }, region });
          newRequestedRegions.push(region);
        }
      }

      setRequestedRegions(newRequestedRegions);
    };

    return <OriginalReactComponent {...otherProps} />;
  };

  WithGlobalMids.propTypes = {
    basePath: PropTypes.string.isRequired,
    getMerchantRegion: PropTypes.func.isRequired,
    getSelectableMerchants: PropTypes.func.isRequired,
    merchantsWithRegion: PropTypes.array,
    mids: PropTypes.array,
    selectableMids: PropTypes.array,
  };

  return compose(
    clientComponents.withPromiseBasedCapabilities({
      getSelectableMerchants: CAPS.V2_MERCHANTS_GET_ALL,
    }),
    coreComponents.withTokenHelper(tokenHelper => ({
      mids: tokenHelper.getMids(),
    })),
    connect(state => ({
      basePath: `/${coreSelectors.slug(state)}`,
      getMerchantRegion: coreSelectors.getMerchantRegion(state),
      merchantsWithRegion: coreSelectors.getMerchants(state),
      selectableMids: getSelectableMids(state),
      selectedMid: getSelectedMid(state),
      hasSelectedMid: hasSelectedMid(state),
    })),
    withLifecycle({
      async onWillMount({ selectedMid, basePath, hasSelectedMid, history, mids }) {
        if (mids?.length > 1 && !IS_BPC_APP) {
          if ((!selectedMid && !hasSelectedMid) || (selectedMid && !mids.includes(selectedMid))) {
            history.push(`${basePath}/${APP_PATHS.MID_SELECTOR}`);
          }
        }
      },
    }),
  )(WithGlobalMids);
};
